import React from 'react';
import { SectionRecommendation } from '@/api/types';
import { RichTextDisplay } from '@/components/ui/RichTextDisplay';
import Markdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

interface SuggestionsTabProps {
  recommendations: SectionRecommendation[];
}

export function AnalysisSuggestionsTab({ recommendations }: SuggestionsTabProps) {
  return (
    <div className="space-y-6">
      {recommendations.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">No section recommendations provided.</p>
      ) : (
        Object.entries(
          recommendations.reduce((acc, rec) => {
            const s = rec.section || 'General';
            if (!acc[s]) acc[s] = [];
            acc[s].push(rec);
            return acc;
          }, {} as Record<string, SectionRecommendation[]>)
        ).map(([section, recs]) => (
          <div key={section} className="space-y-3">
            <h3 className="text-sm font-semibold tracking-wider text-slate-700 dark:text-slate-300 border-b border-slate-300 dark:border-slate-800 pb-2">
              {section} Suggestions
            </h3>
            <div className="grid gap-3">
              {(recs as SectionRecommendation[]).map((rec, i) => (
                <div key={i} className="p-4 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${
                      rec.priority === 'high' ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20' : 
                      rec.priority === 'medium' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-500 border-amber-200 dark:border-amber-500/20' : 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-500/20'
                    }`}>
                      {rec.priority}
                    </span>
                    <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200">{(rec as any).title || rec.section}</h4>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-rose-50/50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-800">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Issue</div>
                      <div className="text-[14px] text-slate-700 dark:text-slate-300 leading-relaxed max-w-none prose prose-sm dark:prose-invert">
                        <Markdown rehypePlugins={[rehypeSanitize]}>{rec.issue}</Markdown>
                      </div>
                    </div>

                    <div className="p-3 bg-emerald-50/50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-800">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Recommendation</div>
                      <div className="text-[14px] text-slate-700 dark:text-slate-300 leading-relaxed max-w-none prose prose-sm dark:prose-invert">
                        <Markdown rehypePlugins={[rehypeSanitize]}>{rec.recommendation}</Markdown>
                      </div>
                    </div>
                  </div>

                  {rec.refinedText && <RichTextDisplay text={rec.refinedText} title="Refined Text" />}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
